import { documentToReactComponents, NodeRenderer, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import { logger } from '@soluto-home-web/platform-utils';
import { Entry } from 'contentful';
import React, { ElementType, Fragment } from 'react';
import get from 'lodash/get';
import ErrorBoundary from './ErrorBoundary';

export type ContentRenderer<TProps = any, TFields = TProps> = {
  component: ElementType<TProps>;
  convertProps?: (fields: Entry<TFields>) => { key?: string } & TProps;
};

const internalRendererOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: ({
      data: {
        target: { fields },
      },
    }) => <img loading="lazy" alt={fields.title} src={fields.file.url} />,
    [INLINES.HYPERLINK]: ({ data: { uri } }, children) => {
      return (
        <a href={uri} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  },
  renderText: (text) => {
    const sentences = text.split('\n');
    return (
      <>
        {sentences[0]}
        {sentences.slice(1).map((s, i) => (
          <Fragment key={i}>
            <br />
            {s}
          </Fragment>
        ))}
      </>
    );
  },
};

const createNodeRenderer = (renderers: Record<string, ContentRenderer>): NodeRenderer => (node, children) => {
  const contentType = get(node, ['data', 'target', 'sys', 'contentType', 'sys', 'id']);
  const renderer = renderers[contentType];
  if (!renderer) {
    logger.warn(`renderer not found for ${contentType}`);
    return null;
  }
  const { convertProps, component: Component } = renderer;
  const target = get(node, ['data', 'target']) || { fields: {}, sys: {} };

  const props = convertProps ? convertProps(target) : target.fields;

  if (children && (!Array.isArray(children) || children.length > 0)) {
    props.children = children;
  }

  return (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
};

export type RichTextProps = {
  blockRenderers?: Record<string, ContentRenderer>;
  inlineRenderers?: Record<string, ContentRenderer>;
  content: Document;
  rendererOptions?: Options;
};

const RichText = ({ content, blockRenderers = {}, inlineRenderers = {}, rendererOptions = {} }: RichTextProps) => {
  const options: Options = {
    ...internalRendererOptions,
    ...rendererOptions,
    renderMark: {
      ...internalRendererOptions.renderMark,
      ...rendererOptions.renderMark,
    },
    renderNode: {
      ...internalRendererOptions.renderNode,
      [BLOCKS.EMBEDDED_ENTRY]: createNodeRenderer(blockRenderers),
      [INLINES.EMBEDDED_ENTRY]: createNodeRenderer(inlineRenderers),
      ...rendererOptions.renderNode,
    },
  };

  return <>{documentToReactComponents(content, options)}</>;
};

export default RichText;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/RichText.tsx